<template>
  <div class="summary">
    <currency-select v-if="!isAuth" data-attr="cs-currency-select" index="cs"/>

    <h2>{{ $t('cartSummary.totalDue')}}:</h2>

    <ul class="summary-list">
      <li
          v-show="!item.isHidden && (item.title || item?.data?.length)"
          v-for="(item, key) in summaryList"
          :key="key"
          :data-attr="`cs-${item.key}`"
      >

        <div v-if="item.title && domainType !== $options.DOMAIN_TYPE.CLOUD">
          <h3 class="title" >
            <i v-if="isGift(item)" class="gift sr-icon-gift" />
            <template v-if="item?.key?.includes('payment-period')">{{item.title}} </template>
            <template v-else>{{ $t( 'cartSummary.domain.'+item.title) }} </template>
            <div class="name">
              <i v-if="domainType === $options.DOMAIN_TYPE.SSL || item?.type === $options.DOMAIN_TYPE.SSL" class="sr-icon-lock-check" />
              <strong v-html="item.name"></strong> /
              <span
                  class="item-period"
                  v-html="item?.key?.includes('payment-period') ? $t('periods.' + item.period) : `${item.year || '0.00'} ${$tc('year', item.year)}`"
              ></span>
            </div>
          </h3>
        </div>

        <ul class="extra">
          <li
              v-for="(val, index) in item.data"
              :key="index"
              :data-attr="`cs-${val.id}`"
              :class="val.id"
          >
            <div class="title" >
              <v-icon v-if="val.id === 'whois'" class="sr-icon-shield"/>
              <span v-html="val.name"></span>
            </div>

            <div v-if="val.qty" v-html="`${val.qty} x ${priceTransform(currencyInfo.rate, currencyInfo.suffix, '', currencyInfo.prefix)}`"> </div>
          </li>
        </ul>

        <div class="price">
          <span v-if="isGift(item) && item.basePrice" class="old-price">
            {{ numberTransform(getTotalBase(item)) }}
          </span>
          <span
          v-html="priceTransform(getItemPrice(item.key, item.price, item.data), currencyInfo.suffix, '', currencyInfo.prefix)"
          :data-attr="`price-${item.key}`"
          :data-attr-price="`${currencyInfo.prefix || ''}${getItemPrice(item.key, item.price, item.data)} ${currencyInfo.suffix || ''}`"
          ></span>
        </div>

        <div
            v-if="!isGift(item) && Number(item.renewal) > 0"
            class="renewal"
            :data-attr-price="`${currencyInfo.prefix || ''}${totalRenewal(item)} ${currencyInfo.suffix || ''}`"
        >
          {{$t('renewalPrice')}} {{currencyInfo.prefix}}{{numberTransform(totalRenewal(item))}} {{currencyInfo.suffix}}
        </div>
      </li>
    </ul>

    <v-expansion-panels class="promo-panel" v-model="panel" multiple>
      <v-expansion-panel>
        <v-expansion-panel-header v-slot="{ open }" data-attr="promo-activator">
          <div class="trigger">
            <div>{{ $t('cartSummary.addPromo') }}<br></div>
            <div
                v-if="discount && !open"
                class="discount"
                data-attr="discount-added"
                :data-attr-price="`${currencyInfo.prefix || ''}${discount} ${currencyInfo.suffix || ''}`"
            >{{ $t('cartSummary.discount') }}:&nbsp;{{currencyInfo.prefix}}<b>{{discount}}</b> {{currencyInfo.suffix}}</div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-form ref="form" class="promo-holder" @submit.prevent="checkPromo">
            <v-text-field
                v-model="promo"
                :loading="loading"
                outlined
                clearable
                data-attr="promo"
                autofocus
            >
              <template v-slot:message="{ message }">
                <span v-html="message"></span>
              </template>
            </v-text-field>
            <v-btn
                :ripple="false"
                @click="checkPromo"
                :loading="loading"
                color="primary"
                data-attr="promo-btn"
            >
              <i class="sr-icon-plus"></i>
            </v-btn>
          </v-form>

          <v-expand-transition>
            <template v-if="promo && isAvailablePromo !== undefined">
              <div v-if="isAvailablePromo" class="info-message valid" data-attr="promo-valid">
                <v-icon class="sr-icon-check"/>
                <span
                    v-html="$t('cartSummary.promoSuccess', {promo: promo, discount: discount, currencySuffix: currencyInfo.suffix, currencyPrefix: currencyInfo.prefix})"
                    data-attr="discount"
                    :data-attr-price="`${currencyInfo.prefix || ''}${discount} ${currencyInfo.suffix || ''}`"
                ></span>
              </div>
              <div v-else class="info-message invalid" data-attr="promo-invalid">
                <v-icon class="sr-icon-close"/>
                <span v-html="$t('validation.promo.'+promoError, {promo: promo, periods: variablesList(promoCycles)})"></span>
              </div>
            </template>
          </v-expand-transition>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <div class="total">
      <div class="head">{{ $t('cartSummary.total') }}</div>
      <div class="price-row">
        <v-tooltip left max-width="330">
          <template v-slot:activator="{ on }">
            <i class="sr-icon-lock" v-on="on"></i>
          </template>
          <span>{{ $t('cartSummary.infoProtection') }}</span>
        </v-tooltip>
        <span
            v-html="priceTransform(totalPrice,  currencyInfo.suffix, '', currencyInfo.prefix)"
            class="price"
            :data-attr-price="`${currencyInfo.prefix || ''}${totalPrice} ${currencyInfo.suffix || ''}`"
        ></span>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import { checkPromoCode } from "@/store/api/settings";
  import { DOMAIN_TYPE, PROMO, PROMO_TYPE } from '@/constants';
  import { numberTransform } from '@/modules/numberTransform';
  import { priceTransform } from '@/modules/priceTransform';
  import CurrencySelect from '@/shared/components/CurrencySelect/CurrencySelect.vue';
  import { CART_GETTERS, CART_ACTIONS, CART_MUTATIONS } from "@/store/modules/cart-context";
  import { SETTINGS_GETTERS, SETTING_ACTIONS, SETTINGS_MUTATIONS } from "@/store/modules/settings";
  import * as Sentry from "@sentry/vue";

  export default {
    name: "CartSummary",
    model: {
      prop: 'items'
    },
    DOMAIN_TYPE,
    components: { CurrencySelect },
    props: {
      summaryList: { type: Array, default: () => ([]) },
      currencyInfo: { type: [Object, Array], default: () => ({}) },
      routeQuery: { type: Object, default: () => ({}) },
      paymentPeriods: { type: Array, default: () => ([]) },
      payType: { type: String },
      domainType: { type: String },
      monthlyDiscount40: { type: Boolean, default: false },
    },

    data: () => ({
      loading: false,
      promo: '',
      panel: undefined,
      discount: 0,
      discountRenewal: 0,
      isAvailablePromo: undefined,
      promoError: undefined,
      extraPrice: {},
      promoResponse: undefined,
      freeProduct: false,
      promoCycles: [],
      totalPrice: 0,
    }),

    mounted() {
      if (!this.cartContext) { this.cartContext = this.setСartContext(); }
      if (!this.cartExtra) { this.cartExtra = this.setCartExtra(); }
      if (!this.authorization) { this.authorization = this.setAuth(); }
    },

    computed: {
      ...mapGetters('cartContext', {
        cartContext: CART_GETTERS.CART_CONTEXT,
        cartExtra: CART_GETTERS.CART_EXTRA,
      }),
      ...mapGetters('settings', {
        authorization: SETTINGS_GETTERS.AUTH,
      }),
      isAuth() {
        return this.authorization && this.authorization.auth && !!Object.keys(this.authorization.auth).length || false;
      },
      freeTrial() {
        return this.isAvailablePromo && this.promo && this.promo.toLowerCase().includes(PROMO.FREE_TRIAL) || false;
      },
    },
    methods: {
      numberTransform,
      priceTransform,
      ...mapActions('cartContext', {
        setСartContext: CART_ACTIONS.SET_CART_CONTEXT,
        setCartExtra: CART_ACTIONS.SET_CART_EXTRA,
      }),
      variablesList(list){
        const boldList = list.map( period => `<b>${period}</b>` );
        return boldList.slice(0, -1).join(', ').concat(boldList?.length > 1 ? ` ${this.$t('or')} ` : '', boldList.slice(-1));
      },
      totalRenewal(item) {
        let dataSum = 0;
        if (Array.isArray(item.data)) {
          item.data.forEach(obj => { dataSum += obj.price });
        }
        const renewalSum = dataSum + item.renewal;

        if (this.promoResponse?.recurring) {
          const discountRenewal = Math.min(this.discountRenewal, renewalSum);
          return (Math.round((renewalSum - discountRenewal) * 100) / 100).toFixed(2);
        }
        return renewalSum;
      },
      prefilledPromo() {
        this.promo = this.routeQuery.promo;
        this.checkPromo();
        if (!this.promo) { this.panel = [1] };
      },
      async checkPromo() {
        this.promo = (this.promo || '').toString().trim();
        if (!this.promo) return
        try {
          this.loading = true;
          this.promoResponse = await checkPromoCode(encodeURI(this.promo));
          this.isAvailablePromo = true;

          this.promoCycles = [];

          if (this.promoResponse?.cycles?.products?.length) {
            this.promoCycles.push(...this.promoResponse.cycles.products.map(el => this.$t(`paymentPeriods.${el}`)));
          }
          this.getPromo(this.promoResponse);
        } catch (error) {
          Sentry.captureException(error);
          this.isAvailablePromo = false;

          if(error.status === 404) {
            this.promoError = 'notExist';
          } else if(error.status === 410) {
            this.promoError = 'expired';
          } else if (error.status === 400) {
            this.promoError = 'maxusesReached'
          } else if (error.status === 425) {
            this.promoError = 'tooEarly'
          } else {
            this.promoError = 'notExist';
          }
        } finally {
          this.$emit('currentPromo', this.promo);
          this.loading = false;
        }
      },
      getPromo(promo) {
        this.discount = 0;
        this.discountRenewal = 0;
        this.promoError = undefined;

        this.getPromoStatus(promo);
      },
      getPromoStatus(promo) {
        this.isAvailablePromo = true;
        const paymentPeriodsArr = [];

        Object.values(this.paymentPeriods).forEach(item => paymentPeriodsArr.push(item.period));
        const periodsHasCycles = promo.appliesto.pid.includes(this.cartContext.products[0].pid) && promo.cycles.products.some(product => paymentPeriodsArr?.includes(product));

        let hasRequiredCycle = false;
        hasRequiredCycle = (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && ((promo.cycles.products.length === 0 && promo.cycles.domains.length === 0) || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) || hasRequiredCycle;

        this.cartContext.add_products.forEach(() => {
          hasRequiredCycle = (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && ((promo.cycles.products.length === 0 && promo.cycles.domains.length === 0) || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) || hasRequiredCycle;
        });

        this.cartContext.domains.forEach((domain) => {
          const domainTld = domain.domain ? domain.domain.substr(domain.domain.indexOf('.') + 1) : '';
          hasRequiredCycle = (promo.appliesto.tld.includes(domainTld) && ((promo.cycles.products.length === 0 && promo.cycles.domains.length === 0) || promo.cycles.domains.includes(domain.billingcycle))) || hasRequiredCycle;
        });

        if ((!periodsHasCycles && !hasRequiredCycle) ||
          (this.isAuth && this.authorization.auth.active_orders > 0 && promo.newsignups) ||
          ((!this.isAuth || this.isAuth && this.authorization.auth.active_orders === 0) && promo.existingclient)
        ) {
          this.isAvailablePromo = false;
          this.promoError = 'cantUse';
        }

        else if (!hasRequiredCycle && periodsHasCycles) {
          this.isAvailablePromo = false;
          this.promoError = 'invalidPromo';
        }

        else if(!promo.cycles.products.includes('one time') && this.payType === DOMAIN_TYPE.ONETIME) {
          this.isAvailablePromo = false;
          this.promoError = 'invalidPeriod';
        }

        if (this.isAuth && promo.onceperclient && this.authorization.auth.used_promo.length > 0 && this.authorization.auth.used_promo.includes(promo.id)) {
          this.isAvailablePromo = false;
          this.promoError = 'oneTime';
        }

        if (this.isAvailablePromo) {
          this.getExtraData(this.cartExtra);
          this.getPromoValue(promo);
        }
      },
      getPromoValue(promo) {
        const productObj = this.summaryList.find((el) => el.key === 'payment-period');
        const productUpsellPrice = this.summaryList?.find((el) => el.key === 'upsell-payment-period')?.price || 0;
        const domainObj = this.summaryList.find((el) => el.key === 'domain-name');
        const productPrice = (productObj?.price || 0) + (this.extraPrice.hostingPrice || 0);
        const domainPrice = (domainObj?.price || 0) + (this.extraPrice.domainPrice || 0);
        const domainPriceRenewal = (domainObj?.renewal || 0) + (this.extraPrice.domainPrice || 0);

        let discount = 0;
        let discountRenewal = 0;

        switch (promo.type) {
          case PROMO_TYPE.PERCENTAGE:
            if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
              const productDiscount = (productPrice + productUpsellPrice) * promo.value / 100;
              discount += productDiscount;
            }
            this.cartContext.add_products.forEach((prod) => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
                discount += prod.price * promo.value / 100;
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                discount += domainPrice * promo.value / 100;
                if (promo?.recurring && domainPrice !== domainPriceRenewal) {
                  discountRenewal += domainPriceRenewal * promo.value / 100;
                }
              }
            });
            break;
          case PROMO_TYPE.FIXED_AMOUNT:
            if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 ||
              promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
              discount += this.convertCurrency(promo.value);
            }
            this.cartContext.add_products.forEach(() => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
                discount += this.convertCurrency(promo.value);
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                discount += this.convertCurrency(promo.value);
              }
            });
            break;
          case PROMO_TYPE.PRICE_OVERRIDE:
            if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
              const val = productPrice < this.convertCurrency(promo.value) ? productPrice : productPrice - this.convertCurrency(promo.value);
              discount += val;
            }
            this.cartContext.add_products.forEach((prod) => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
                const val = prod.price < this.convertCurrency(promo.value) ? prod.price : prod.price - this.convertCurrency(promo.value);
                discount += val;
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                const calculateDiscount = (price) => price < this.convertCurrency(promo.value)
                  ? price
                  : price - this.convertCurrency(promo.value);
                discount += calculateDiscount(domainPrice);
                if (promo?.recurring && domainPrice !== domainPriceRenewal) {
                  discountRenewal += calculateDiscount(domainPriceRenewal);
                }
              }
            });
            break;
          case PROMO_TYPE.FREE_SETUP:
            if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
              discount += productPrice;
            }
            this.cartContext.add_products.forEach((prod) => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0].pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0].billingcycle))) {
                discount += prod.price;
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                discount += domainPrice;
                if (promo?.recurring && domainPrice !== domainPriceRenewal) {
                  discountRenewal += domainPriceRenewal;
                }
              }
            });
            break;
          default: break;
        }

        discount = discount ? (Math.round(discount * 100) / 100).toFixed(2) : '0.00';
        discountRenewal = discountRenewal ? (Math.round(discountRenewal * 100) / 100).toFixed(2) : discount;
        this.discount = discount;
        this.discountRenewal = discountRenewal;
        this.getTotalPrice();
      },
      convertCurrency(val) {
        const fixedValue = Object.values(this.promoResponse.fixed_values).find(item => item.currency === this.cartContext.currency);
        const value = Object.keys(this.currencyInfo).length ? val * this.currencyInfo.rate : fixedValue.price;
        return Number((Math.round(value * 100) / 100).toFixed(2));
      },
      getTotalPrice() {
        const sum = this.summaryList.length > 0 ? this.summaryList.reduce((a, b) => (a || 0) + Number(b.price || 0), 0) + this.extraPrice.total : 0;
        this.discount = this.discount > sum ? sum : this.discount;
        const totalPrice = (Math.round((sum - this.discount) * 100) / 100).toFixed(2);
        this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_TOTAL_PRICE}`, totalPrice);
        this.totalPrice = totalPrice;

        const domainObj = this.summaryList.find((el) => el.key === 'domain-name');

        const domainPrice = domainObj && domainObj.price ? Number(domainObj.price) + (this.extraPrice.domainPrice || 0) : 0;
        const freeDomain = domainPrice <= 0;
        this.$emit('freeDomain', freeDomain);
      },
      getExtraData(obj) {
        Object.values(this.summaryList).forEach(item => {
          if(item?.key === "domain-name") {
            this.$set(item, 'data', obj.domain)
          }
          else if(item?.key === "payment-period") {
            this.$set(item, 'data', obj.hosting)
          }
        });
        const domainPrice = this.cartExtra.domain.length > 0 ? this.cartExtra.domain.reduce((a, b) => (a || 0) + Number(b.price || 0), 0) : 0;
        const hostingPrice = this.cartExtra.hosting.length > 0 ? this.cartExtra.hosting.reduce((a, b) => (a || 0) + Number(b.price || 0), 0) : 0;
        this.extraPrice = {
          domainPrice,
          hostingPrice,
          total: domainPrice + hostingPrice
        }
      },
      getItemPrice(key, item, extra = []) {
        const itemPrice = this.freeProduct && key === 'payment-period' ? 0 : Number(item);
        const extraPriceTotal = extra.reduce((total, current) => total + parseFloat(current.price), 0);
        return (Math.round((itemPrice + extraPriceTotal) * 100) / 100).toFixed(2);
      },
      getTotalBase(item) {
        const basePrice = Number(item.basePrice) || 0;
        const dataSum = (item.data || []).reduce((sum, obj) => sum + obj.price, 0);
        return (Math.round((dataSum + basePrice) * 100) / 100).toFixed(2);
      },
      isGift(item) {
        const itemPrice = (this.freeProduct && item?.key === 'payment-period') ? 0 : Number(item.price);
        return itemPrice === 0;
      }
    },
    watch : {
      promo() {
        this.promoResponse = undefined;
        this.isAvailablePromo = undefined;
        this.discount = 0;
        this.freeProduct = false;
        this.getTotalPrice();
        this.$emit('currentPromo', '');
      },
      isAvailablePromo(available) {
        this.$store.commit(`cartContext/${CART_MUTATIONS.SET_CART_CONTEXT}`, { promo: available === true ? this.promo : ''});
      },
      authorization: {
        handler() {
          if(this.promoResponse) { this.getPromo(this.promoResponse) }
        },
        deep: true,
      },
      cartContext: {
        handler() {
          if(this.promoResponse) { this.getPromo(this.promoResponse) }
        },
        deep: true,
      },
      cartExtra: {
        handler(obj) {
          this.getExtraData(obj);
        },
        deep: true,
      },
      summaryList: {
        handler() {
          if(this.promoResponse) { this.getPromo(this.promoResponse) }
          this.getExtraData(this.cartExtra);
          this.getTotalPrice();
        },
        deep: true,
      },
      freeTrial() {
        this.$emit('freeTrial', this.freeTrial);
      },
      promoError(val) {
        if(val && !(this.monthlyDiscount40 && this.cartContext.products[0].billingcycle !== 'monthly')) {
          this.panel = [0];
        }
      },
      'routeQuery.promo': {
        handler() {
          this.prefilledPromo();
        },
      },
    },
  }
</script>

<style lang="scss">
  .inner-wrapper-sticky {
    padding-top: 12px;
  }

  .summary {
    background-color: $white;
    padding: 12px 14px 28px;
    margin-bottom: 68px;
    position: relative;

    @media (max-width: $desktop) {
      margin: 12px 0;
      padding: 19px 19px 28px;
    }

    &:before,
    &:after {
      content: '';
      position: absolute;
      height: 12px;
      width: 100%;
      left: 0;
      background-image: url('~@/assets/images/summary-border.svg');
      background-repeat: repeat-x;
    }

    &:before {
      top: -12px;
    }

    &:after {
      bottom: -12px;
      transform: scale(1, -1);
    }

    h2 {
      font-size: 24px;
      margin-left: 12px;
      margin-bottom: 20px;
    }

    .summary-list {
      padding-bottom: $row-padding;
    }

    .total {
      text-align: right;
      font-weight: 700;
    }

    .head {
      padding-bottom: 2px;
    }

    .price-row {
      display: flex;
      justify-content: flex-end;
      align-items: center;

      i {
        color: $blue;
        margin-right: 12px;
        font-size: 40px;

        &:hover {
          cursor: pointer;
        }
      }

      .price {
        font-size: 36px;
      }

      .price-transform {
        display: flex;
        flex-wrap: nowrap;
        align-items: flex-end;
      }

      sub {
        font-size: 19px;
        display: block;
        margin-left: 3px;
        margin-bottom: 7px;
        line-height: .9;

        span {
          font-weight: 400;
          font-size: 14px;
          display: block;
        }
      }
    }

    .price {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .currency-select-cs {
      margin-left: auto;
      margin-bottom: 5px;
    }
  }

  ul.summary-list {
    padding-left: 0;

    @media (max-width: $desktop) {
      display: flex;
      flex-wrap: wrap;
    }

    > li {
      padding: 5px 12px 14px;
      border-bottom: 1px dashed $gray-soft;

      &:not(:last-child) {
        margin-bottom: 7px;
      }
    }

    li {
      list-style: none;
      width: 100%;
    }

    .title:not(:empty) {
      font: 19px/1.23 $base-font-family !important;
      margin-bottom: -4px;
      letter-spacing: 0 !important;
      padding-bottom: 10px;

        .gift {
          display: inline-flex;
          align-content: center;
          justify-content: center;
          flex-wrap: wrap;
          vertical-align: 2px;
          width: 22px;
          height: 20px;
          border-radius: 4px;
          margin-right: 6px;
          font-size: 12px;
          background: $primary;
          color: $white;
      }
    }

    .name {
      font-size: 14px;

      strong {
        font-size: 19px;
        margin-right: 2px;
        word-wrap: break-word;
      }
    }

    .item-period {
      white-space: nowrap;
    }

    .price {
      padding-bottom: 3px;
      text-align: right;
    }

    .old-price {
      color: $gray-text;
      margin-right: 12px;
      font-size: 13px;
      text-decoration: line-through;
    }

    .renewal {
      text-align: right;
      color: $gray-text;
      font-size: 13px;
      margin-left: auto;
      line-height: 1.1;
    }

    .extra {
      padding-left: 0;

      .title {
        font-size: 14px !important;
      }
    }


    .name,
    .extra .title {

      i {
        font-size: 14px;
        margin-right: 4px;
        color: $black;
        display: inline-block;
        vertical-align: text-top;
      }
    }
  }

  .promo-panel {

    .theme--light.v-expansion-panels {
      margin-bottom: $row-padding;

      .v-expansion-panel-header__icon {
        margin-top: -4px;
      }
    }

    .v-expansion-panel-header {
      > *,
      .v-icon {
        color: rgba($black, .5)  !important;
      }
    }

    form {
      display: flex;
    }

    .v-input__control {

      .v-input__slot {
        min-height: 50px !important;
      }
    }

    .v-btn.primary {
      min-width: 50px !important;
      padding: 4px 14px 4px !important;
      margin-bottom: 8px;
      margin-left: 7px;
    }

    .discount {
      color: $green;
      margin-top: 6px;
    }

    .trigger {
      display: block !important;
    }

    .info-message {
      background: red;

      b {
        word-wrap: break-word;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: all .5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>
